import React, { useState } from "react";
import { ChevronDown, HelpCircle } from "lucide-react";

const FAQ_ITEMS = [
  {
    q: "¿Pre-Legal.cl reemplaza a un abogado?",
    a: "No. Pre-Legal.cl es un asistente de inteligencia artificial especializado en derecho chileno que te ayuda a entender tu situación, conocer tus derechos y preparar tu caso antes de una consulta. No reemplaza el juicio profesional, la representación ante tribunales ni la firma de un abogado colegiado. Su objetivo es convertirte en un cliente informado para que aproveches mejor cada minuto (y cada peso) con tu abogado.",
  },
  {
    q: "¿Mis consultas son confidenciales?",
    a: "Sí. Todas tus consultas son privadas y no se comparten con terceros ni se utilizan para entrenar modelos externos. La plataforma está diseñada exclusivamente para consultas legales, por lo que tu información no se mezcla con otros usos. Puedes consultar nuestra Política de Privacidad para más detalles sobre cómo protegemos tus datos.",
  },
  {
    q: "¿Qué tipo de consultas puedo hacer?",
    a: "Puedes hacer consultas sobre las principales áreas del derecho chileno: laboral (despidos, indemnizaciones, finiquitos), arrendamiento (contratos, desahucios, garantías), familia (pensiones, divorcios, cuidado personal), consumidor (Ley del Consumidor, SERNAC, garantías), penal (denuncias, querellas, delitos comunes) y tributario (impuestos, SII, declaraciones). También puedes pedir redacción de cartas, demandas simples, escritos y análisis de contratos bajo la legislación chilena.",
  },
  {
    q: "¿Cómo funciona el plan gratuito?",
    a: "El plan Gratis te permite hacer 3 preguntas de prueba sin necesidad de ingresar tarjeta de crédito ni datos de pago. Tienes acceso completo al corpus de jurisprudencia y normativa chilena durante esas 3 preguntas. Una vez utilizadas, las preguntas no se renuevan — son solo para que evalúes la calidad del servicio antes de suscribirte al plan mensual.",
  },
  {
    q: "¿La información está actualizada con la ley chilena vigente?",
    a: "Sí. Pre-Legal.cl está conectado a la Biblioteca del Congreso Nacional (BCN) para leyes y normativas vigentes, y al Poder Judicial de Chile para jurisprudencia real con número de Rol y fecha exacta. Nuestro corpus se actualiza cada semana, incorporando nuevos fallos, reformas legales y dictámenes. Si una norma cambia o si el modelo no encuentra información verificada, lo dice explícitamente — nunca inventa respuestas.",
  },
  {
    q: "¿Puedo cancelar mi suscripción cuando quiera?",
    a: "Sí, totalmente. No hay contratos de permanencia ni letra chica. Puedes cancelar tu suscripción en cualquier momento directamente desde tu cuenta, y mantendrás el acceso hasta el final del período ya pagado. No se realizan cobros adicionales después de cancelar. Aceptamos pagos con Flow.cl y Mercado Pago (tarjetas de crédito, débito, Webpay y transferencias).",
  },
];

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const toggle = (i) => setOpenIndex(openIndex === i ? -1 : i);

  // Schema.org FAQPage JSON-LD for Google rich snippets
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ_ITEMS.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a,
      },
    })),
  };

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="py-20 px-5 sm:px-8"
    >
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <div
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-4"
            style={{
              background: "var(--pl-primary-soft)",
              color: "var(--pl-primary)",
            }}
            data-testid="faq-badge"
          >
            <HelpCircle className="w-3.5 h-3.5" />
            Preguntas frecuentes
          </div>
          <h2
            className="text-3xl sm:text-4xl font-bold tracking-tight mb-3"
            style={{
              color: "var(--pl-text-strong)",
              letterSpacing: "-0.02em",
            }}
          >
            Lo que más nos preguntan
          </h2>
          <p
            className="text-base max-w-xl mx-auto"
            style={{ color: "var(--pl-text-muted)" }}
          >
            Resolvemos las dudas más comunes antes de que empieces.
          </p>
        </div>

        <div className="space-y-3" data-testid="faq-list">
          {FAQ_ITEMS.map((item, i) => {
            const isOpen = openIndex === i;
            return (
              <div
                key={i}
                data-testid={`faq-item-${i}`}
                className="rounded-xl overflow-hidden transition-all"
                style={{
                  background: isOpen
                    ? "linear-gradient(135deg, rgba(0,200,255,0.06) 0%, rgba(0,200,255,0.015) 100%)"
                    : "var(--pl-card)",
                  border: isOpen
                    ? "1px solid var(--pl-primary)"
                    : "1px solid var(--pl-border)",
                  boxShadow: isOpen
                    ? "0 10px 30px rgba(0, 200, 255, 0.08)"
                    : "var(--pl-shadow-sm)",
                }}
              >
                <button
                  type="button"
                  onClick={() => toggle(i)}
                  data-testid={`faq-toggle-${i}`}
                  aria-expanded={isOpen}
                  aria-controls={`faq-answer-${i}`}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left transition-colors"
                  style={{
                    color: isOpen ? "var(--pl-primary)" : "var(--pl-text-strong)",
                  }}
                >
                  <span className="text-base sm:text-[17px] font-semibold leading-snug">
                    {item.q}
                  </span>
                  <ChevronDown
                    className="w-5 h-5 shrink-0 transition-transform duration-300"
                    style={{
                      transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                      color: isOpen ? "var(--pl-primary)" : "var(--pl-text-muted)",
                    }}
                  />
                </button>
                <div
                  id={`faq-answer-${i}`}
                  data-testid={`faq-answer-${i}`}
                  className="overflow-hidden transition-all duration-300 ease-out"
                  style={{
                    maxHeight: isOpen ? "500px" : "0px",
                    opacity: isOpen ? 1 : 0,
                  }}
                >
                  <div
                    className="px-5 pb-5 text-sm sm:text-[15px] leading-relaxed"
                    style={{ color: "var(--pl-text-muted)" }}
                  >
                    {item.a}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <p
          className="text-center mt-10 text-sm"
          style={{ color: "var(--pl-text-muted)" }}
        >
          ¿Otra pregunta?{" "}
          <a
            href="mailto:soporte@acbothub.com"
            data-testid="faq-contact-link"
            className="font-semibold underline decoration-dotted underline-offset-4 transition-colors"
            style={{ color: "var(--pl-primary)" }}
          >
            Escríbenos a soporte@acbothub.com
          </a>
        </p>
      </div>
    </section>
  );
};

export default FAQ;
